'use client';

import { useEffect } from 'react';

const MAX_ERRORS_PER_PAGE = 10;
const DEDUP_WINDOW_MS = 5000;

export function MatomoErrorTracker() {
  useEffect(() => {
    // Skip error tracking on admin routes
    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/d9k2m')) return;

    let errorCount = 0;
    const recentMessages = new Map<string, number>();

    function isDuplicate(message: string): boolean {
      const lastSeen = recentMessages.get(message);
      const now = Date.now();
      if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) return true;
      recentMessages.set(message, now);
      return false;
    }

    // First-party fallback beacon: same signal reaches the server even when
    // Matomo is blocked. Fired alongside (not instead of) the _paq event, and
    // only after the same dedup/rate guards, so we don't double-count in Matomo
    // or flood the server. Best-effort — never let it throw into a handler.
    function sendBeacon(message: string) {
      try {
        const payload = JSON.stringify({
          type: 'error',
          path: window.location.pathname,
          message,
        });
        navigator.sendBeacon?.('/api/rum', new Blob([payload], { type: 'application/json' }));
      } catch {
        // ignore — beacon is a best-effort supplement to Matomo
      }
    }

    function handleError(event: ErrorEvent) {
      if (errorCount >= MAX_ERRORS_PER_PAGE) return;
      const message = event.message || 'Unknown error';
      if (isDuplicate(message)) return;
      errorCount++;

      const label = event.filename
        ? `${message} at ${event.filename}:${event.lineno}:${event.colno}`
        : message;

      window._paq?.push(['trackEvent', 'Error', 'JSError', label, event.lineno || 0]);
      sendBeacon(label);
    }

    function handleRejection(event: PromiseRejectionEvent) {
      if (errorCount >= MAX_ERRORS_PER_PAGE) return;
      const reason = event.reason instanceof Error
        ? event.reason.message
        : String(event.reason || 'Unknown rejection');
      if (isDuplicate(reason)) return;
      errorCount++;

      window._paq?.push(['trackEvent', 'Error', 'UnhandledRejection', reason]);
      sendBeacon(reason);
    }

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, []);

  return null;
}
